/**
 * Audio slicing — the APP cuts the audio, never the model.
 *
 * Pipeline used by `firstPass.ts`:
 *   1. `decodeAudioBlob(blob)`      -> AudioBuffer (Web Audio decodeAudioData)
 *   2. `encodeForUpload(buffer)`    -> small mono 16 kHz WAV to send to Gemini
 *   3. `sliceSegmentsToWav(...)`    -> one mono 16-bit PCM WAV Blob per segment
 *
 * Everything here is dependency-free: the WAV encoder is written inline so we
 * never ship an encoder library.
 */

/*
 * INTERNAL PRIMITIVES, un-exported in the restructure: `getAudioContext`,
 * `resampleLinear`, `encodeWavMono16`. Each has callers inside this file and
 * had none outside it — exporting them invited a second WAV-writing path, and
 * "one WAV surface" is the whole point of this module. `durationOf` (a one-line
 * `buffer.duration` wrapper with no callers at all) was deleted.
 */

/* ------------------------------------------------------------------ */
/* AudioContext plumbing                                               */
/* ------------------------------------------------------------------ */

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

let sharedCtx: AudioContext | null = null;

function audioContextCtor(): AudioContextCtor {
  const w = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) throw new Error('This browser has no Web Audio API, so audio cannot be decoded.');
  return Ctor;
}

/**
 * A single lazily-created AudioContext reused for every decode.
 *
 * Browsers cap the number of live contexts (Safari especially), and creating
 * one per file leaks them. Decoding works while the context is suspended, so
 * we never need a user gesture just to cut clips.
 */
function getAudioContext(): AudioContext {
  if (!sharedCtx || sharedCtx.state === 'closed') {
    const Ctor = audioContextCtor();
    sharedCtx = new Ctor();
  }
  return sharedCtx;
}

/* ------------------------------------------------------------------ */
/* Decoding                                                            */
/* ------------------------------------------------------------------ */

/**
 * Decode an uploaded/recorded audio Blob into an AudioBuffer.
 *
 * Throws a human-readable error when the browser cannot decode the container
 * (most common cause: an exotic codec, or a zero-byte recording).
 */
export async function decodeAudioBlob(blob: Blob): Promise<AudioBuffer> {
  if (!blob || blob.size === 0) throw new Error('That audio file is empty.');

  const bytes = await blob.arrayBuffer();
  const ctx = getAudioContext();

  try {
    // Promise form (modern) with a callback fallback for older Safari.
    const decoded = await new Promise<AudioBuffer>((resolve, reject) => {
      const maybe = ctx.decodeAudioData(bytes, resolve, reject) as unknown;
      if (maybe && typeof (maybe as Promise<AudioBuffer>).then === 'function') {
        (maybe as Promise<AudioBuffer>).then(resolve, reject);
      }
    });
    if (!decoded || decoded.length === 0) throw new Error('decoded to zero samples');
    return decoded;
  } catch (err) {
    const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
    throw new Error(
      `Could not decode this audio${detail}. Try a WAV, MP3, M4A, OGG or FLAC file.`,
    );
  }
}

/* ------------------------------------------------------------------ */
/* Sample extraction                                                   */
/* ------------------------------------------------------------------ */

/**
 * Downmix `[startSec, endSec)` of a buffer to a single mono Float32Array.
 * Out-of-range requests are clamped; an empty range yields a 1-sample buffer
 * so downstream encoders always produce a valid (if tiny) WAV.
 */
export function extractMono(buffer: AudioBuffer, startSec = 0, endSec = buffer.duration): Float32Array {
  const rate = buffer.sampleRate;
  const total = buffer.length;

  let from = Math.floor(Math.max(0, startSec) * rate);
  let to = Math.ceil(Math.min(buffer.duration, Math.max(startSec, endSec)) * rate);
  from = Math.min(Math.max(0, from), total);
  to = Math.min(Math.max(from, to), total);

  // degenerate window (empty file, or a request past the end): return silence
  if (to <= from) return new Float32Array(1);

  const out = new Float32Array(to - from);
  const channels = buffer.numberOfChannels;

  if (channels === 1) {
    buffer.copyFromChannel(out, 0, from);
    return out;
  }

  const scratch = new Float32Array(to - from);
  for (let ch = 0; ch < channels; ch++) {
    buffer.copyFromChannel(scratch, ch, from);
    for (let i = 0; i < scratch.length; i++) out[i] += scratch[i];
  }
  const inv = 1 / channels;
  for (let i = 0; i < out.length; i++) out[i] *= inv;
  return out;
}

/**
 * Cheap linear-interpolation resampler. Good enough for speech we only ever
 * send to a speech model — we never resample the clips the human listens to.
 */
function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = input[idx] ?? 0;
    const b = input[idx + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/** Peak-normalize in place, but only when the material is quiet. */
function gentleNormalize(samples: Float32Array): Float32Array {
  let peak = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = Math.abs(samples[i]);
    if (v > peak) peak = v;
  }
  if (peak === 0 || peak > 0.5) return samples;
  const gain = Math.min(6, 0.9 / peak);
  for (let i = 0; i < samples.length; i++) samples[i] *= gain;
  return samples;
}

/* ------------------------------------------------------------------ */
/* WAV encoding (mono, 16-bit PCM)                                     */
/* ------------------------------------------------------------------ */

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}

/** Wrap mono float samples in a canonical 44-byte-header PCM WAV. */
function encodeWavMono16(samples: Float32Array, sampleRate: number): Blob {
  // The header stores the rate as an integer, so round ONCE and derive the byte
  // rate from the rounded value — otherwise a fractional rate (Safari's
  // AudioContext, a resampler) writes a sampleRate and a byteRate that disagree
  // and some decoders play the clip at the wrong speed.
  const rate = Math.max(1, Math.round(Number.isFinite(sampleRate) ? sampleRate : 44100));
  const dataBytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');

  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // format = PCM
  view.setUint16(22, 1, true); // channels = mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const raw = samples[i];
    // NaN/Infinity would round-trip through setInt16 as 0/garbage depending on
    // the engine; pin them to silence explicitly.
    const s = Number.isFinite(raw) ? Math.max(-1, Math.min(1, raw)) : 0;
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/* ------------------------------------------------------------------ */
/* Public slicing API                                                  */
/* ------------------------------------------------------------------ */

export interface SliceOptions {
  /** Seconds of context added on both sides so words don't sound chopped. */
  padSec?: number;
  /** Never produce a clip shorter than this (seconds). */
  minSec?: number;
  /** Resample clips to this rate. Omit to keep the source rate. */
  targetSampleRate?: number;
  /** Boost very quiet clips so they're audible on a phone speaker. */
  normalize?: boolean;
}

const DEFAULT_PAD_SEC = 0.06;
const DEFAULT_MIN_SEC = 0.12;

/** Cut one `[startSec, endSec]` window out of a decoded buffer as a WAV Blob. */
export function sliceToWav(
  buffer: AudioBuffer,
  startSec: number,
  endSec: number,
  options: SliceOptions = {},
): Blob {
  const {
    padSec = DEFAULT_PAD_SEC,
    minSec = DEFAULT_MIN_SEC,
    targetSampleRate,
    normalize = true,
  } = options;

  const duration = buffer.duration;
  let from = Math.max(0, Math.min(duration, startSec) - padSec);
  let to = Math.min(duration, Math.max(startSec, endSec) + padSec);

  // grow a too-short window symmetrically, then clamp back inside the file
  if (to - from < minSec) {
    const grow = (minSec - (to - from)) / 2;
    from = Math.max(0, from - grow);
    to = Math.min(duration, to + grow);
    if (to - from < minSec) from = Math.max(0, to - minSec);
  }

  let samples = extractMono(buffer, from, to);
  let rate = buffer.sampleRate;

  if (targetSampleRate && targetSampleRate !== rate) {
    samples = resampleLinear(samples, rate, targetSampleRate);
    rate = targetSampleRate;
  }
  if (normalize) samples = gentleNormalize(samples);

  return encodeWavMono16(samples, rate);
}

/** A time window to cut. Deliberately structural so it accepts Segments too. */
export interface SliceRequest {
  startSec: number;
  endSec: number;
}

export interface SliceAllOptions extends SliceOptions {
  /** Called after each clip with 1-based `done` and the total. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Cut every requested window. Yields to the event loop periodically so a long
 * task (hundreds of clips) doesn't freeze the UI on a phone.
 */
export async function sliceSegmentsToWav(
  buffer: AudioBuffer,
  requests: SliceRequest[],
  options: SliceAllOptions = {},
): Promise<Blob[]> {
  const { onProgress, ...sliceOptions } = options;
  const out: Blob[] = [];

  for (let i = 0; i < requests.length; i++) {
    const r = requests[i];
    out.push(sliceToWav(buffer, r.startSec, r.endSec, sliceOptions));
    onProgress?.(i + 1, requests.length);
    if ((i + 1) % 12 === 0) await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return out;
}

/**
 * Encode a whole decoded file as a compact mono 16 kHz WAV.
 *
 * This is what we hand to Gemini: it normalizes away every container/codec
 * quirk (webm/opus recordings, m4a, flac …) into one MIME type the API
 * definitely accepts, and shrinks the base64 payload by ~10x versus the
 * original file for typical stereo 44.1 kHz audio.
 */
export function encodeForUpload(buffer: AudioBuffer, sampleRate = 16000): Blob {
  const mono = extractMono(buffer, 0, buffer.duration);
  const resampled = resampleLinear(mono, buffer.sampleRate, sampleRate);
  return encodeWavMono16(resampled, sampleRate);
}

/**
 * `m:ss.s` — one decimal, for clip boundaries where tenths matter.
 * Used by the Verify and Correct cards; {@link formatDuration} is the
 * whole-second form used for file/track lengths.
 */
export function formatTimecode(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00.0';
  // Round to tenths FIRST: rounding after the split renders 59.96 as "0:60.0".
  const tenths = Math.round(sec * 10);
  const m = Math.floor(tenths / 600);
  const s = (tenths - m * 600) / 10;
  return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
}

/** `mm:ss` (or `h:mm:ss`) for durations shown in the UI. */
export function formatDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '0:00';
  const s = Math.floor(totalSec % 60);
  const m = Math.floor((totalSec / 60) % 60);
  const h = Math.floor(totalSec / 3600);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}
